import { supabase } from '@/integrations/supabase/client';

/**
 * Logs messages with a timestamp
 * @param message The message to log
 * @param optionalParams Additional parameters to log
 */
export function logWithTimestamp(message: string, ...optionalParams: any[]) {
  const now = new Date();
  const timestamp = now.toISOString();
  console.log(`${timestamp}: ${message}`, ...optionalParams);
}

/**
 * Validates if the provided user type is allowed in the system
 * @param userType The user type to validate
 * @returns Boolean indicating if the user type is valid
 */
export function isValidUserType(userType: string | null | undefined): boolean {
  if (!userType) return false;
  
  // These are the currently supported user types
  // Note: This list should match the database constraint
  return ['admin', 'free', 'starter', 'pro', 'premium', 'standard', 'analyst'].includes(userType);
}

/**
 * Determines if an email belongs to an admin user
 * @param email The email to check
 * @returns Boolean indicating if the email belongs to an admin
 */
export function isAdminEmail(email: string | undefined): boolean {
  if (!email) return false;
  
  // Normalize the email address to ensure consistent comparison
  const normalizedEmail = email.trim().toLowerCase();
  
  // Admin email addresses - add additional addresses if needed
  const adminEmails = ['kintupercy@gmail.com'];
  
  return adminEmails.includes(normalizedEmail);
}

/**
 * Checks if a profile exists for the given user ID
 * @param userId The ID of the user to check
 * @returns Promise that resolves to a boolean indicating if the profile exists
 */
export async function checkProfileExists(userId: string): Promise<boolean> {
  try {
    logWithTimestamp(`Checking if profile exists for user: ${userId}`);
    
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();
      
    if (error && error.code !== 'PGRST116') {
      logWithTimestamp('Error checking if profile exists:', error);
      return false;
    }
    
    return !!data;
  } catch (err) {
    logWithTimestamp('Unexpected error checking profile:', err);
    return false;
  }
}

/**
 * Enum for authentication events
 */
export enum AuthEvent {
  SIGNED_IN = 'SIGNED_IN',
  SIGNED_OUT = 'SIGNED_OUT',
  USER_UPDATED = 'USER_UPDATED',
  USER_DELETED = 'USER_DELETED',
  PASSWORD_RECOVERY = 'PASSWORD_RECOVERY',
  TOKEN_REFRESHED = 'TOKEN_REFRESHED',
  INITIAL_SESSION = 'INITIAL_SESSION'
}

/**
 * Log authentication events with structured data
 */
export function logAuthEvent(
  event: AuthEvent, 
  userId: string | null, 
  metadata: Record<string, any> | null = null
) {
  const eventData = {
    event,
    userId,
    timestamp: new Date().toISOString(),
    ...(metadata ? { metadata } : {})
  };
  
  logWithTimestamp(`Auth event: ${event}`, eventData);
  
  // Could be extended to send events to analytics or monitoring system
}
